import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  MapPin,
  MessagesSquare,
  Mic,
  Scale,
  Sparkles,
  Users,
  PartyPopper,
  Presentation,
  Network,
} from "lucide-react";
import AnimatedWaves from "../components/AnimatedWaves";
import HeroCursorGlow from "../components/HeroCursorGlow";
import BridgeSlideshow from "../components/BridgeSlideshow";
import Reveal from "../components/Reveal";
import ImpactStats from "../components/ImpactStats";
import {
  BRAND,
  MISSION,
  EVENT_TYPES,
  PAST_EVENTS,
  MINDSET_INTRO,
} from "../mock";

const EVENT_ICONS = {
  Discussions: MessagesSquare,
  "Interactive Forums": Users,
  Socials: PartyPopper,
  "Expert Panels": Presentation,
  Debates: Scale,
  Speakers: Mic,
  "Networking Events": Network,
};

export default function Home() {
  const recent = PAST_EVENTS.slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        <BridgeSlideshow />
        <div className="absolute inset-0 stars-bg opacity-30" />
        <div className="absolute inset-0 opacity-50">
          <AnimatedWaves />
        </div>
        <HeroCursorGlow />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#0a3d5e] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 w-full">
          <Reveal delay={50}>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-300/5 px-4 py-1.5 text-[12px] tracking-[0.2em] uppercase text-cyan-100 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-cyan-200" />
              Chartered BridgeUSA chapter at Columbia
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="font-serif-display font-bold text-white leading-[0.98] text-[56px] sm:text-[78px] lg:text-[108px] tracking-tight">
              <span className="block">
                Turning disagreement{" "}
                <span className="hl-block italic">into</span>
              </span>
              <span className="hl-block-deep mt-4 inline-block">dialogue.</span>
            </h1>
          </Reveal>

          <Reveal delay={260}>
            <p className="mt-10 max-w-xl text-slate-200 text-lg font-serif-body">
              {BRAND.tagline}. {BRAND.subtagline}
            </p>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="group inline-flex items-center gap-3 rounded-full border border-cyan-300/70 pl-6 pr-2 py-2 text-slate-100 hover:border-cyan-200 hover:bg-cyan-300/10 transition-all btn-shine"
              >
                <span className="font-serif-body text-lg">Learn More</span>
                <span className="w-10 h-10 rounded-full bg-cyan-300 text-[#062a40] flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <ArrowRight className="w-5 h-5" />
                </span>
              </Link>
              <Link
                to="/get-involved"
                className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-5 py-3 text-slate-100 hover:bg-white/10 transition-colors font-serif-body"
              >
                Get Involved
              </Link>
            </div>
          </Reveal>

          {/* Scroll hint */}
          <div className="absolute left-6 lg:left-10 bottom-8 hidden md:flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-cyan-200/70">
            <span className="w-10 h-[1px] bg-cyan-200/50" />
            Scroll
          </div>
        </div>
      </section>

      {/* MISSION TEASER */}
      <section className="relative py-12 sm:py-20 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">
              Who We Are
            </p>
            <h2 className="font-serif-display text-5xl lg:text-6xl font-semibold leading-[1.02] text-white">
              The Columbia chapter of{" "}
              <span className="hl-block-deep">BridgeUSA.</span>
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-8 space-y-6 text-slate-200 font-serif-body text-lg leading-relaxed">
            <p className="text-white text-xl">{MISSION.short}</p>
            <p>{MISSION.paragraphs[1]}</p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-4 text-cyan-200 hover:text-cyan-100 link-underline"
            >
              Read our full story <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Impact stats */}
      <Reveal>
        <ImpactStats />
      </Reveal>

      {/* BRIDGE MINDSET */}
      <section className="relative py-12 sm:py-20 md:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">
              The Bridge Mindset
            </p>
            <h2 className="font-serif-display text-5xl lg:text-6xl font-semibold leading-[1.02] text-white">
              A shared <span className="hl-block-deep">mindset</span>, not shared answers.
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 text-slate-200 font-serif-body text-lg leading-relaxed">
            <p>{MINDSET_INTRO}</p>
            <Link
              to="/about#norms"
              className="mt-6 inline-flex items-center gap-2 text-cyan-200 hover:text-cyan-100 link-underline"
            >
              See our discussion norms <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="relative py-12 sm:py-20 md:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">
              What We Do
            </p>
            <h2 className="font-serif-display text-5xl lg:text-6xl font-semibold text-white leading-[1.02]">
              Seven kinds of <span className="hl-block-deep">gatherings.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {EVENT_TYPES.map((t, i) => {
              const Icon = EVENT_ICONS[t.title] || MessagesSquare;
              return (
                <Reveal key={i} delay={i * 100}>
                  <article className="group rounded-2xl border border-white/15 bg-[#145074] p-8 hover:border-cyan-300/60 hover:bg-[#1a618d] transition-all h-full">
                    <div className="w-12 h-12 rounded-xl bg-cyan-300/15 border border-cyan-300/40 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-200" />
                    </div>
                    <h3 className="mt-6 font-serif-display text-3xl text-white">
                      {t.title}
                    </h3>
                    <p className="mt-4 text-slate-200 font-serif-body leading-relaxed">
                      {t.body}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="relative py-12 sm:py-20 md:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">
                Recent Events
              </p>
              <h2 className="font-serif-display text-5xl lg:text-6xl font-semibold text-white leading-[1.02]">
                What we’ve <span className="hl-block-deep">hosted.</span>
              </h2>
              <p className="mt-4 text-slate-300 max-w-xl">
                New programming for the fall term will be announced soon — follow us on Instagram or apply for the fellowship to stay in the loop.
              </p>
            </div>
            <Link
              to="/events"
              className="text-cyan-200 hover:text-cyan-100 inline-flex items-center gap-2 link-underline"
            >
              View all notable events <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {recent.map((e, i) => (
              <Reveal key={i} delay={i * 100}>
                <article className="rounded-2xl border border-white/15 bg-[#145074] p-7 hover:border-cyan-300/60 hover:bg-[#1a618d] transition-colors h-full">
                  <div className="flex items-center gap-2 text-xs text-cyan-200 uppercase tracking-[0.2em]">
                    <Calendar className="w-3.5 h-3.5" /> {e.date}
                  </div>
                  <h3 className="mt-5 font-serif-display text-[26px] leading-tight text-white">
                    {e.title}
                  </h3>
                  <p className="mt-3 text-slate-300 text-sm">{e.host}</p>
                  <div className="mt-6 flex items-center gap-4 text-sm text-slate-200">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> {e.location}
                    </span>
                    <span className="text-slate-400">{e.time}</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-12 sm:py-20 md:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-cyan-300/30 bg-gradient-to-br from-[#196d9c] to-[#0a3d5e] p-10 md:p-16">
              <div className="absolute inset-0 stars-bg opacity-40" />
              <div className="relative max-w-3xl">
                <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-5">
                  Get Involved
                </p>
                <h2 className="font-serif-display text-5xl md:text-6xl font-semibold leading-[1.02] text-white">
                  Join the <span className="hl-block-deep">Bridge community.</span>
                </h2>
                <p className="mt-6 text-slate-200 text-lg font-serif-body">
                  Become a member, attend events, and connect with students from across the ideological spectrum.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <a
                    href={BRAND.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-cyan-300 hover:bg-cyan-200 text-[#062a40] px-6 py-3 font-medium transition-colors btn-shine"
                  >
                    Follow us on Instagram <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link
                    to="/get-involved"
                    className="inline-flex items-center gap-2 rounded-full border border-cyan-300/70 hover:border-cyan-200 px-6 py-3 text-slate-100 hover:bg-cyan-300/10 transition-colors"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
